import { ApiService } from './api.service';
import { Injectable } from '@angular/core';
import { LoginForm, LoginResponse } from '../models/authentication.model';
import { Observable, map, take, tap } from 'rxjs';
import { SESSION_ID, TOKEN_NAME } from '../constants/authentication.constants';
import { Router } from '@angular/router';
import { DialogService } from './dialog.service';
import { CookieService } from './cookie-service.service';
import { WindowService } from './window.service';
import { ToastService } from './toast.service';
import { OnlineClientService } from './online-client.service';
import { AutologinUrlsModel } from '../models/autologin.model';
import { SessionService } from './session.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  error = '';
  timeTillExpiry = 480;
  warningTimeBeforeExpiry = 5;
  timeTillExpiryWarning = 475;
  expiryTimer: ReturnType<typeof setTimeout> | null = null;
  confirmationDialogOpenTimer: ReturnType<typeof setTimeout> | null = null;
  tokenExpirationTimeInMinutes = 0;
  private initials = '';
  private staffId = '';
  private firmId = '';
  private isFreeTrial = '';
  private aiSession = '';
  private autologinUrls: AutologinUrlsModel | undefined;

  constructor(
    private api: ApiService,
    private router: Router,
    private dialogService: DialogService,
    private cookieService: CookieService,
    private windowService: WindowService,
    private toastService: ToastService,
    private sessionService: SessionService
  ) {}

  public login(credentials: LoginForm) {
    return this.api.post<LoginResponse>('login', credentials, { showToast: false, withCredentials: true }).pipe(
      map((res) => {
        this.tokenExpirationTimeInMinutes = res.tokenExpirationTimeInMinutes;
        this.initExpiryTimeout(res.tokenExpirationTimeInMinutes);
        this.initials = res.initials;
        this.staffId = res.staffId;
        this.firmId = res.firmId;

        if (res.licenseCountExceeded && res.activeSessions && res.activeSessions.length > 0) {
          this.sessionService.openSessionTakeoverDialog(res.activeSessions);
        }

        //TO-DO: Verify this does not remove session Id from browser
        console.log(`---> Saving Session Id: ${res.sessionId} in authentication.service login`);
        this.windowService.setSessionIdToWindow(res.sessionId);

        return res;
      })
    );
  }

  setAISession(session: string): void {
    this.aiSession = session;
  }

  getAISession(): string {
    return this.aiSession;
  }

  public logout(timedOut = false) {
    // console.log(`---> Logout called in authentication.service`);
    this.api.sendBeacon('logout', {});

    this.handleLogout(true, timedOut);
  }

  public handleLogout(gracefulLogout = true, timedOut = false) {
    // console.log(`---> HandleLogout called in authentication.service`);
    this.clearTimers();
    this.dialogService.closeDialogs('HandleLogout');

    console.log(`---> HandleLogout called and is calling removeUserData`);

    this.removeUserData();

    if (!gracefulLogout) {
      this.toastService.show('Unauthorized. Please login.', { classname: 'bg-danger text-light', autohide: false });
    }
    //console.log(`---> HandleLogout called and is calling navigate to loggedout`);

    if (timedOut) {
      this.router.navigate(['/timedout']);
    } else {
      this.router.navigate(['/loggedout']);
    }
  }

  get isLoggedIn() {
    const token = this.cookieService.getCookie(TOKEN_NAME) ?? null;
    const sessionId = this.windowService.getSessionIdFromWindow() ?? null;

    //console.log(`---> IsLoggedIn Called..\nToken is: ${token}\nSession is: ${sessionId}`);

    if (token && sessionId) {
      const parsedToken = this.parseJwt(token);
      this.initials = parsedToken.Initials;
      this.staffId = parsedToken.StaffGuid;
      this.firmId = parsedToken.FirmId;
      this.isFreeTrial = parsedToken.IsFreeTrial;
      return true;
    }
    return false;
  }

  public clearJWT() {
    this.cookieService.clearCookie(TOKEN_NAME);
  }

  public getInitials() {
    return this.initials;
  }

  public getStaffId() {
    return this.staffId;
  }

  public getFirmId() {
    return this.firmId;
  }

  public getIsFreeTrial() {
    return this.isFreeTrial.toLowerCase() === 'true';
  }

  public forgotPassword() {
    window.location.href = 'https://anywhere.txdocs.com/forgotpassword';
  }

  public forgotFirmId() {
    window.location.href = 'https://anywhere.txdocs.com/forgotFirmID';
  }

  private initExpiryTimeout(tokenExpirationTimeInMinutes: number) {
    this.timeTillExpiry = tokenExpirationTimeInMinutes;
    this.timeTillExpiryWarning = this.timeTillExpiry - this.warningTimeBeforeExpiry;
    if (this.timeTillExpiryWarning > 0) {
      this.setExpiryTimeout();
    }
  }

  public setExpiryTimeout() {
    // console.log(
    //   `---> Setting expiry timeout for ${this.timeTillExpiryWarning} minutes or at`,
    //   new Date(new Date().getTime() + this.timeTillExpiryWarning * 60 * 1000).toLocaleTimeString()
    // );
    this.clearTimers();
    this.expiryTimer = setTimeout(() => this.showExpiryWarningDialog(), this.timeTillExpiryWarning * 60 * 1000);
  }

  public getUserDetails() {
    const token = this.cookieService.getCookie(TOKEN_NAME);
    if (token) {
      const parsedToken = this.parseJwt(token);
      return parsedToken;
    }
  }

  private showExpiryWarningDialog() {
    if (!this.dialogService.isDialogOpen('sessionExpiryDialog')) {
      this.dialogService.confirm(
        {
          title: `Session expiring soon`,
          message: `Without activity your session expires within ${this.warningTimeBeforeExpiry} minutes. Would you like to extend your session?`,
          confirmCallback: () => {
            this.setExpiryTimeout();
            return this.keepAlive().subscribe();
          },
          rejectCallback: (reason) => {
            if (reason != 'HandleLogout') {
              this.logout();
            }
          },
        },
        'sessionExpiryDialog'
      );
    }

    // create autologin and urls that will be used when the session times out
    this.createAutologin().pipe(take(1)).subscribe();

    // automatically logout if dialog is open and timeTillExpiry passed
    this.confirmationDialogOpenTimer = setTimeout(() => this.logout(true), this.warningTimeBeforeExpiry * 60 * 1000);
  }
  // public saveUserData(token: any) {
  //   sessionStorage.setItem(TOKEN_NAME, token);
  // }

  public removeUserData() {
    console.log(
      `---> Removing Session Id: ${this.windowService.getSessionIdFromWindow()} in authentication.service removeUserData`
    );
    this.windowService.clearSessionIdFromWindow();
  }

  private keepAlive() {
    return this.api.post<any>('keep-alive', {});
  }

  public clearTimers() {
    // console.log(`---> Clearing timers`);
    this.expiryTimer && clearTimeout(this.expiryTimer);
    this.confirmationDialogOpenTimer && clearTimeout(this.confirmationDialogOpenTimer);
  }

  public getAutologinUrls(): AutologinUrlsModel | undefined {
    return this.autologinUrls;
  }

  private createAutologin(): Observable<AutologinUrlsModel> {
    return this.api.post<AutologinUrlsModel>('autologin/create', {}).pipe(
      tap((response: AutologinUrlsModel) => {
        this.autologinUrls = response;
      })
    );
  }

  private parseJwt(token: string): any {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );

    return JSON.parse(jsonPayload);
  }
}
